/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { InformationFilled } from '@carbon/react/icons';
import { ListItem, OrderedList, PasswordInput } from 'carbon-components-react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { links } from '../links';
import { FIELDS } from '../schema';
import { AppRegistrationMessages as messages } from './messages';

export const AppInfoContent = ({
  isQuickSetup,
  readOnly
}: ChannelContentProps) => {
  const formMethods = useFormContext();
  const { registerInputField } = useFormHelpers(formMethods);

  const appIdChangeState = useState<boolean>(false);
  const tenantIdChangeState = useState<boolean>(false);
  const appPasswordChangeState = useState<boolean>(false);

  return (
    <OrderedList className="TabContent__subtitle TabContent__link-larger-text">
      <ListItem>
        <FormattedMessageWithLink
          url={links.MICROSOFT_AZURE_LINK}
          {...messages.descriptionOne}
        />
      </ListItem>
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionTwo} />
      </ListItem>
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionThree} />
        <div className="TabContent__spacer16" />
        <PasswordInput
          {...registerInputField({
            field: FIELDS.APP_ID,
            isQuickSetup,
            isPasswordField: true,
            labelText: messages.appId,
            placeholderText: messages.appIdPlaceholder,
            changeState: appIdChangeState,
            readOnly
          })}
        />
      </ListItem>
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionSix} />
        <div className="TabContent__spacer16" />
        <PasswordInput
          {...registerInputField({
            field: FIELDS.TENANT_ID,
            isQuickSetup,
            isPasswordField: true,
            labelText: messages.tenantId,
            placeholderText: messages.tenantIdPlaceholder,
            changeState: tenantIdChangeState,
            readOnly
          })}
        />
      </ListItem>
      <ListItem>
        <FormattedMessage
          values={{
            value: <strong>{messages.value.defaultMessage}</strong>
          }}
          {...messages.descriptionFive}
        />
        <div className="TabContent__spacer16" />
        <PasswordInput
          {...registerInputField({
            field: FIELDS.APP_PASSWORD,
            isQuickSetup,
            isPasswordField: true,
            labelText: messages.clientSecret,
            placeholderText: messages.appPasswordPlaceholder,
            changeState: appPasswordChangeState,
            readOnly
          })}
        />
        <div className="TabContent__spacer16" />
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <InformationFilled size={16} className="TabContent__info-icon" />
          <FormattedMessage {...messages.expirationNote} />
        </div>
      </ListItem>
    </OrderedList>
  );
};
